import { keys } from 'ramda';
import { MessageDescriptor } from 'react-intl';

export type ErrorTranslations = {
  [key: string]: MessageDescriptor;
  default: MessageDescriptor;
};

type CustomError = {
  humanized?: {
    rowData: Record<string, string>;
  };
  error?: {
    errorType: string;
  };
  data?: {
    error?: {
      errorType: string;
      values: Record<string, string>;
      status?: string;
    };
  };
  body: {
    humanized?: {
      rowData: Record<string, string>;
    };
    data?: {
      error: {
        errorType: string;
        values: Record<string, string>;
        status?: string;
      };
    };
    error: {
      errorType: string;
      values: Record<string, string>;
      status?: string;
    };
  };
};

export function createTranslatedError(
  error: CustomError,
  translations: ErrorTranslations = { default: {} },
  defaultTranslation?: MessageDescriptor
) {
  const customError = error.body?.error ?? error.body?.data?.error ?? error.data?.error ?? error.error;

  if (customError?.errorType) {
    const translation = translations[customError.errorType];
    return createTranslatedErrorWithValues(translation, translations, customError?.values);
  }

  if (customError?.status) {
    const translation = translations[customError.status];

    return createTranslatedErrorWithValues(translation, translations, customError?.values);
  }

  const rowData = error.body?.humanized?.rowData ?? error.humanized?.rowData;
  if (rowData) {
    const translation = translations['missing-columns'];

    return createTranslatedErrorWithValues(translation, translations, {
      columns: keys(rowData).join(', '),
    });
  }

  return (
    defaultTranslation ?? {
      id: 'error.default',
      defaultMessage: 'Něco se pokazilo. Zkuste to znovu.',
    }
  );
}

function transformArrayToStrings<T extends Record<string, unknown>>(data: T): T {
  const result = { ...data } as T;

  for (const [key, value] of Object.entries(result)) {
    if (Array.isArray(value)) {
      (result as Record<string, unknown>)[key] = value.join(', ');
    }
  }

  return result;
}

function createTranslatedErrorWithValues(
  translation: MessageDescriptor,
  translations: ErrorTranslations,
  values: Record<string, string> | undefined
): MessageDescriptor {
  if (!translation) {
    return translations.default;
  }

  if (!values) {
    return translation;
  }

  return {
    ...translation,
    values: transformArrayToStrings(values),
  } as MessageDescriptor;
}
